import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RankingsService } from "../../../../../core/http/services/RankingsService";
import { RoundsService } from "../../../../../core/http/services/RoundsService";
import { MatchesService } from "../../../../../core/http/services/MatchesService";
import { CategoriesService } from "../../../../../core/http/services/CategoriesService";
import { PlayersService } from "../../../../../core/http/services/PlayersService";

const initializeSeasonUiState = () => ({
  expanded: false,
  selectedRound: null,
  selectedCategory: "",
  searchPlayer: "",
});

export default function useAdminMatches() {
  const [seasons, setSeasons] = useState([]);
  const [seasonUi, setSeasonUi] = useState({});
  const seasonUiRef = useRef({});

  const [roundsByRankingId, setRoundsByRankingId] = useState({});
  const [seasonGamesResponse, setSeasonGamesResponse] = useState({});
  const [lineDrafts, setLineDrafts] = useState({});
  const [lineOriginalResults, setLineOriginalResults] = useState({});
  const [sentLines, setSentLines] = useState({});

  useEffect(() => {
    seasonUiRef.current = seasonUi;
  }, [seasonUi]);

  const loadSeasonGames = useCallback(async (seasonId, roundId) => {
    if (!roundId) return;

    setSeasonGamesResponse((prev) => ({
      ...prev,
      [seasonId]: { ...(prev[seasonId] || {}), loading: true },
    }));

    const { status, data } = await RoundsService.getById(roundId);

    if (status !== 200) {
      setSeasonGamesResponse((prev) => ({
        ...prev,
        [seasonId]: { loading: false, payload: { groups: [] } },
      }));
      return;
    }

    const groups = (data?.groups ?? []).map((group) => ({
      lineId: group.id,
      categoryId: group.categoryId,
      games: (group.matches ?? []).map((match) => ({
        gameId: match.id,
        teamA: [match.player1?.name, match.player2?.name].filter(Boolean),
        teamB: [match.player3?.name, match.player4?.name].filter(Boolean),
        finished: match.finished ?? false,
        result: {
          teamA: match.team1Score ?? "",
          teamB: match.team2Score ?? "",
        },
      })),
    }));

    setSeasonGamesResponse((prev) => ({
      ...prev,
      [seasonId]: { loading: false, payload: { groups } },
    }));

    // Ao carregar uma rodada, sincroniza "originais" e drafts com o que veio da API.
    // Isso evita comparar/mandar PATCH para jogos que o usuário não alterou (ex.: draft antigo).
    setLineOriginalResults((prev) => {
      const updated = { ...prev };
      groups.forEach((line) => {
        updated[line.lineId] = updated[line.lineId] || {};
        line.games.forEach((game) => {
          updated[line.lineId][game.gameId] = {
            teamA: String(game.result?.teamA ?? ""),
            teamB: String(game.result?.teamB ?? ""),
          };
        });
      });
      return updated;
    });

    setLineDrafts((prev) => {
      const updated = { ...prev };
      groups.forEach((line) => {
        updated[line.lineId] = {};
        line.games.forEach((game) => {
          updated[line.lineId][game.gameId] = {
            teamA: String(game.result?.teamA ?? ""),
            teamB: String(game.result?.teamB ?? ""),
          };
        });
      });
      return updated;
    });
  }, []);

  const fetchRoundsForRanking = useCallback(
    async (rankingId, { loadGames = false } = {}) => {
      const { status, data } = await RankingsService.getRounds(rankingId);
      if (status !== 200) return;

      const rounds = data?.rounds ?? [];
      setRoundsByRankingId((prev) => ({ ...prev, [rankingId]: rounds }));

      const defaultRound = rounds[rounds.length - 1];
      if (!defaultRound) return;

      const currentUi = seasonUiRef.current[rankingId];
      const isCurrentValid = rounds.some((r) => r.id === currentUi?.selectedRound);
      const roundToLoad = isCurrentValid ? currentUi.selectedRound : defaultRound.id;

      if (!isCurrentValid) {
        setSeasonUi((prev) => {
          const current = prev[rankingId];
          if (!current) return prev;
          return { ...prev, [rankingId]: { ...current, selectedRound: defaultRound.id } };
        });
      }

      if (loadGames) {
        loadSeasonGames(rankingId, roundToLoad);
      }
    },
    [loadSeasonGames],
  );

  const fetchRankings = useCallback(async () => {
    const { status, data } = await RankingsService.getAll();
    if (status !== 200) return;

    const mapped = (data ?? []).map((ranking) => ({
      id: ranking.id,
      name: ranking.name,
      isActive: ranking.isActive,
    }));

    setSeasons(mapped);
    setSeasonUi(() => {
      const state = {};
      mapped.forEach((season, index) => {
        state[season.id] = { ...initializeSeasonUiState(), expanded: index === 0 };
      });
      return state;
    });

    if (mapped.length > 0) {
      fetchRoundsForRanking(mapped[0].id, { loadGames: true });
    }
  }, [fetchRoundsForRanking]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    const { status, data } = await CategoriesService.get();
    if (status === 200) setCategories(data ?? []);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const [manageRankModal, setManageRankModal] = useState({
    open: false,
    seasonId: null,
    selectedCategory: "",
  });
  const [manageRankSearch, setManageRankSearch] = useState("");
  const [manageRankPlayers, setManageRankPlayers] = useState([]);

  const fetchManageRankPlayers = useCallback(async (rankingId, categoryId) => {
    if (!rankingId || !categoryId) return;
    const { status, data } = await PlayersService.getAll({ rankingId, categoryId, limit: 100 });
    if (status === 200) {
      const list = Array.isArray(data) ? data : (data?.data ?? data?.players ?? data?.items ?? []);
      setManageRankPlayers(list);
    }
  }, []);

  useEffect(() => {
    if (!manageRankModal.open || !manageRankModal.selectedCategory) return;
    fetchManageRankPlayers(manageRankModal.seasonId, manageRankModal.selectedCategory);
  }, [manageRankModal.open, manageRankModal.selectedCategory, manageRankModal.seasonId, fetchManageRankPlayers]);

  const [addSeasonOpen, setAddSeasonOpen] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState("");

  const [finishModal, setFinishModal] = useState({ open: false, seasonId: null });
  const [newRoundModal, setNewRoundModal] = useState({
    open: false,
    seasonId: null,
    status: null,
    errorMessage: null,
    endsAt: null,
  });

  const selectedSeasonForFinish = useMemo(
    () => seasons.find((season) => season.id === finishModal.seasonId),
    [finishModal.seasonId, seasons],
  );

  const selectedSeasonForNewRound = useMemo(
    () => seasons.find((season) => season.id === newRoundModal.seasonId),
    [newRoundModal.seasonId, seasons],
  );

  const selectedSeasonForManageRank = useMemo(
    () => seasons.find((season) => season.id === manageRankModal.seasonId),
    [manageRankModal.seasonId, seasons],
  );

  const updateSeasonUi = useCallback(
    (seasonId, field, value) => {
      setSeasonUi((prev) => ({
        ...prev,
        [seasonId]: { ...prev[seasonId], [field]: value },
      }));

      if (field === "selectedRound") {
        loadSeasonGames(seasonId, value);
      }
    },
    [loadSeasonGames],
  );

  const handleToggleSeason = useCallback(
    (seasonId, expanded) => {
      setSeasonUi((prev) => ({
        ...prev,
        [seasonId]: { ...prev[seasonId], expanded },
      }));

      if (expanded) {
        fetchRoundsForRanking(seasonId, { loadGames: true });
      }
    },
    [fetchRoundsForRanking],
  );

  const handleScoreChange = useCallback((lineId, matchId, teamField, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;

    setLineDrafts((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        [matchId]: { ...prev[lineId]?.[matchId], [teamField]: value },
      },
    }));
  }, []);

  const canSendLine = useCallback(
    (lineId, line) =>
      line.games.every((game) => {
        const draft = lineDrafts[lineId]?.[game.gameId];
        return Boolean(draft?.teamA?.trim() && draft?.teamB?.trim());
      }),
    [lineDrafts],
  );

  const lineHasChanges = useCallback(
    (lineId, line) =>
      line.games.some((game) => {
        const draft = lineDrafts[lineId]?.[game.gameId];
        const original = lineOriginalResults[lineId]?.[game.gameId];
        if (!draft) return false;
        return (
          String(original?.teamA ?? "") !== draft.teamA ||
          String(original?.teamB ?? "") !== draft.teamB
        );
      }),
    [lineDrafts, lineOriginalResults],
  );

  const handleSendLine = useCallback(
    async (lineId, line) => {
      if (!canSendLine(lineId, line) || !lineHasChanges(lineId, line)) return null;

      const changedGames = line.games.filter((game) => {
        const draft = lineDrafts[lineId]?.[game.gameId];
        const original = lineOriginalResults[lineId]?.[game.gameId];
        if (!draft) return false;
        return (
          String(original?.teamA ?? "") !== draft.teamA ||
          String(original?.teamB ?? "") !== draft.teamB
        );
      });

      for (const game of changedGames) {
        const draft = lineDrafts[lineId]?.[game.gameId];
        const result = await MatchesService.updateResult(game.gameId, {
          team1Score: Number(draft.teamA),
          team2Score: Number(draft.teamB),
        });

        if (result.status !== 200 && result.status !== 201) {
          return {
            error:
              result.data?.message ||
              result.message ||
              "Erro ao enviar resultados.",
          };
        }
      }

      setSeasonGamesResponse((prev) => {
        const updated = { ...prev };

        Object.keys(updated).forEach((seasonId) => {
          const payload = updated[seasonId]?.payload;
          if (!payload?.groups) return;

          updated[seasonId] = {
            ...updated[seasonId],
            payload: {
              ...payload,
              groups: payload.groups.map((existingLine) => {
                if (existingLine.lineId !== lineId) return existingLine;
                return {
                  ...existingLine,
                  games: existingLine.games.map((game) => {
                    const draft = lineDrafts[lineId]?.[game.gameId];
                    if (!draft) return game;
                    return {
                      ...game,
                      result: {
                        teamA: Number(draft.teamA),
                        teamB: Number(draft.teamB),
                      },
                    };
                  }),
                };
              }),
            },
          };
        });

        return updated;
      });

      setLineOriginalResults((prev) => {
        const original = { ...prev };
        original[lineId] = original[lineId] || {};
        line.games.forEach((game) => {
          const draft = lineDrafts[lineId]?.[game.gameId];
          if (!draft) return;
          original[lineId][game.gameId] = { teamA: draft.teamA, teamB: draft.teamB };
        });
        return original;
      });

      setSentLines((prev) => ({ ...prev, [lineId]: true }));
    },
    [canSendLine, lineDrafts, lineHasChanges, lineOriginalResults],
  );

  const handleAddSeason = useCallback(async () => {
    if (!newSeasonName.trim()) return;

    const { status } = await RankingsService.create({
      name: newSeasonName.trim(),
      year: new Date().getFullYear(),
    });

    if (status !== 200 && status !== 201) return;

    setNewSeasonName("");
    setAddSeasonOpen(false);
    fetchRankings();
  }, [newSeasonName, fetchRankings]);

  const handleFinishSeason = useCallback(async () => {
    const seasonId = finishModal.seasonId;
    if (!seasonId) return;

    const { status } = await RankingsService.update(seasonId, { isActive: false });
    if (status !== 200 && status !== 201) return;

    setFinishModal({ open: false, seasonId: null });
    fetchRankings();
  }, [finishModal.seasonId, fetchRankings]);

  const handleCreateNewRound = useCallback(async () => {
    const seasonId = newRoundModal.seasonId;
    if (!seasonId) return;

    setNewRoundModal((prev) => ({ ...prev, status: "loading" }));

    const { status: createStatus, data } = await RankingsService.createRound(seasonId, { endsAt: newRoundModal.endsAt });
    if (createStatus !== 200 && createStatus !== 201) {
      setNewRoundModal((prev) => ({ ...prev, status: "error", errorMessage: data?.message ?? null }));
      return;
    }

    const { status: genStatus, data: genData } = await RoundsService.generate(data.id);
    if (genStatus !== 200 && genStatus !== 201) {
      setNewRoundModal((prev) => ({ ...prev, status: "error", errorMessage: genData?.message ?? null }));
      return;
    }

    setNewRoundModal((prev) => ({ ...prev, status: "success" }));
    fetchRoundsForRanking(seasonId);
    setSeasonUi((prev) => {
      const current = prev[seasonId];
      if (!current) return prev;
      return { ...prev, [seasonId]: { ...current, selectedRound: data.id } };
    });
    loadSeasonGames(seasonId, data.id);
  }, [newRoundModal.seasonId, newRoundModal.endsAt, fetchRoundsForRanking, loadSeasonGames]);

  const handleOpenManageRank = useCallback(
    (seasonId) => {
      const initialCategory = categories[0]?.id ?? "";
      setManageRankSearch("");
      setManageRankModal({ open: true, seasonId, selectedCategory: initialCategory });
    },
    [categories],
  );

  const handleCloseManageRank = useCallback(() => {
    setManageRankModal({ open: false, seasonId: null, selectedCategory: "" });
    setManageRankSearch("");
    setManageRankPlayers([]);
  }, []);

  const handleManageRankCategoryChange = useCallback((categoryId) => {
    setManageRankModal((prev) => ({ ...prev, selectedCategory: categoryId }));
  }, []);

  const handleAddUserToRank = useCallback(
    async (seasonId, userId) => {
      const { status } = await RankingsService.addPlayers(seasonId, [userId]);
      if (status === 200 || status === 201) {
        fetchManageRankPlayers(seasonId, manageRankModal.selectedCategory);
      }
    },
    [fetchManageRankPlayers, manageRankModal.selectedCategory],
  );

  const handleRemoveUserFromRank = useCallback(
    async (seasonId, userId) => {
      const { status } = await RankingsService.removePlayer(seasonId, userId);
      if (status === 204 || status === 200) {
        fetchManageRankPlayers(seasonId, manageRankModal.selectedCategory);
      }
    },
    [fetchManageRankPlayers, manageRankModal.selectedCategory],
  );

  const getSeasonView = useCallback(
    (season) => {
      const ui = seasonUi[season.id] || initializeSeasonUiState();
      const seasonResult = seasonGamesResponse[season.id] || {
        loading: false,
        payload: { groups: [] },
      };

      const allGroups = seasonResult.payload?.groups || [];
      const categoryFiltered = ui.selectedCategory
        ? allGroups.filter((g) => g.categoryId === ui.selectedCategory)
        : allGroups;

      const query = (ui.searchPlayer || "").trim().toLowerCase();
      const lines = !query
        ? categoryFiltered
        : categoryFiltered.filter((line) =>
            line.games.some(
              (game) =>
                game.teamA.some((name) => name.toLowerCase().includes(query)) ||
                game.teamB.some((name) => name.toLowerCase().includes(query)),
            ),
          );

      const rounds = roundsByRankingId[season.id] ?? [];

      return { ui, seasonResult, lines, membersCount: 0, rounds };
    },
    [roundsByRankingId, seasonGamesResponse, seasonUi],
  );

  const getManageRankView = useCallback(() => {
    const seasonId = manageRankModal.seasonId;
    if (!seasonId) return { seasonId: null, usersInRank: [], usersOutRank: [] };

    const query = manageRankSearch.trim().toLowerCase();
    const matchesQuery = (p) => !query || p.name.toLowerCase().includes(query);

    const usersInRank = manageRankPlayers.filter((p) => p.inRanking).filter(matchesQuery);
    const usersOutRank = manageRankPlayers.filter((p) => !p.inRanking).filter(matchesQuery);

    return { seasonId, usersInRank, usersOutRank };
  }, [manageRankModal.seasonId, manageRankPlayers, manageRankSearch]);

  return {
    seasons,
    categories,
    seasonUi,
    lineDrafts,
    sentLines,
    addSeasonOpen,
    newSeasonName,
    finishModal,
    newRoundModal,
    manageRankModal,
    manageRankSearch,
    selectedSeasonForFinish,
    selectedSeasonForNewRound,
    selectedSeasonForManageRank,
    setAddSeasonOpen,
    setNewSeasonName,
    setFinishModal,
    setNewRoundModal,
    setManageRankSearch,
    getSeasonView,
    updateSeasonUi,
    handleToggleSeason,
    handleScoreChange,
    lineHasChanges,
    canSendLine,
    handleSendLine,
    handleAddSeason,
    handleFinishSeason,
    handleCreateNewRound,
    handleOpenManageRank,
    handleCloseManageRank,
    handleManageRankCategoryChange,
    handleAddUserToRank,
    handleRemoveUserFromRank,
    getManageRankView,
  };
}
